import { cloneElement, CSSProperties, ReactElement, useEffect, useRef, useState } from 'react'

import { Option } from 'components/elements/FloatingToolbar/types'

import { useEnableClassNameOnScrollDown, useHideOnScroll, useStopBodyScroll } from 'hooks/useScrollEffects'

import FloatingToolbarGridOption from 'components/elements/FloatingToolbar/GridItem'
import FloatingToolbarListOption from 'components/elements/FloatingToolbar/ListItem'
import FadeInOut from 'components/transitions/FadeInOut'

import styles from './floatingToolbar.module.scss'

const MIN_HEIGHT = 70
const TOTAL_MAX_HEIGHT_PERCENT = 0.75
const JITTER_THRESHOLD = 50

type Props = {
  options: Option[]
  gridColumns?: number

  maxHeightOverride?: number

  includeShrinkOption?: boolean
  noHideOnScroll?: boolean

  hideOnScrollRef?: HTMLDivElement | null // Assumed to be the document if not present

  style?: CSSProperties
  className?: string
  toolbarClassName?: string
  subComponentClassName?: string
}

const FloatingToolbar = ({
  options,
  gridColumns = 4,

  maxHeightOverride,

  includeShrinkOption,
  noHideOnScroll,

  hideOnScrollRef,

  style = {},
  className = '',
  toolbarClassName = '',
  subComponentClassName = '',
}: Props) => {
  const scrollingDown = noHideOnScroll ? false : useHideOnScroll(3, hideOnScrollRef)

  useEnableClassNameOnScrollDown('floating-toolbar', scrollingDown)

  const draggableElement = useRef<HTMLDivElement>(null)
  const openBodyScrollRef = useRef<HTMLDivElement>(null)

  const startingTouchPosition = useRef<number | null>(null)
  const dragOffset = useRef<number | null>(null)
  const startingHeight = useRef<number | null>(null)

  const [open, setOpen] = useState(false)
  const [height, setHeight] = useState(MIN_HEIGHT)
  const [shrunk, setShrunk] = useState(false)

  const [flingMenuOptions, setFlingMenuOptions] = useState<Option[] | null>(null)
  const [maxHeight, setMaxHeight] = useState<number>(getMaxHeight(maxHeightOverride))
  const [subComponent, setSubComponent] = useState<ReactElement | null>(null)

  const setNoScroll = useStopBodyScroll(open)

  const handleTouchStart = (touchEvent: React.TouchEvent) => {
    if (openBodyScrollRef.current?.scrollTop !== 0) return

    startingTouchPosition.current = touchEvent.touches[0]?.clientY
    startingHeight.current = height
  }

  const handleTouch = (touchEvent: React.TouchEvent) => {
    if (startingTouchPosition.current === null) return
    if (startingHeight.current === null) return
    if (openBodyScrollRef.current?.scrollTop !== 0) return

    dragOffset.current = startingTouchPosition.current - touchEvent.touches[0]?.clientY

    // Prevents misfires - don't start listening to drags until we're past our jitter boundry
    // Also allows internal scrolling of elements since it doesn't disable scrolling w/ the .activlyDragging class until we're off of openBodyScroll.scrollTop === 0
    if (Math.abs(dragOffset.current) < JITTER_THRESHOLD) return

    draggableElement.current?.classList.add(styles.activelyDragging)

    handleSetHeight(startingHeight.current + dragOffset.current)
  }

  const handleTouchEnd = () => {
    const dragThreshold = 100
    const swipeUp = (dragOffset.current || 0) > 0
    const swipeThresholdMet = Math.abs(dragOffset.current || 0) > dragThreshold

    if (open && swipeUp && height === maxHeight) return handleResetState()

    if (!swipeThresholdMet) {
      handleSetHeight(open ? maxHeight : MIN_HEIGHT)
      handleResetState()

      return
    }

    handleSetOpen(swipeUp, maxHeight, null, null)
  }

  const handleSetHeight = (desiredHeight: number) => {
    if (desiredHeight > maxHeight) return setHeight(maxHeight)
    if (desiredHeight < MIN_HEIGHT) return setHeight(MIN_HEIGHT)

    setHeight(desiredHeight)
  }

  const handleSetOpen = (
    drawerIsOpen: boolean,
    overideHeight: number = maxHeight,
    options: Option[] | null = null,
    subComponent: ReactElement | null = null,
  ) => {
    if (drawerIsOpen) {
      setMaxHeight(overideHeight)
      handleSetHeight(overideHeight)
      setSubComponent(subComponent)
      setFlingMenuOptions(options)
      setOpen(true)
      setNoScroll(true)

      return handleResetState()
    }

    setMaxHeight(getMaxHeight(maxHeightOverride))
    handleSetHeight(MIN_HEIGHT)
    setSubComponent(null)
    setFlingMenuOptions(null)
    setOpen(false)

    setTimeout(() => setNoScroll(false), 100) // Dont to prevent overscrolling when swipping "away" the drawer

    openBodyScrollRef.current?.scrollTo({ top: 0 })

    handleResetState()
  }

  const handleSetHidden = (hidden: boolean) => {
    setShrunk(hidden)

    if (hidden) setOpen(false)
  }

  const handleOptionClick = (option: Option) => {
    if (option.onClick) return option.onClick()
    if (option.subOptions) return handleSetOpen(true, option.maxHeightOverride, option.subOptions)
    if (option.subComponent) return handleSetOpen(true, option.maxHeightOverride, null, option.subComponent)
  }

  const handleResetState = () => {
    // Always reset state once the drawer has entirely opened/ shut
    dragOffset.current = null
    startingHeight.current = null
    startingTouchPosition.current = null

    draggableElement.current?.classList.remove(styles.activelyDragging)
  }

  const openPercent = height / maxHeight

  return (
    <>
      <div
        className={styles.wrapper}
        onClick={() => handleSetOpen(false, maxHeight, null, null)}
        style={{
          pointerEvents: open ? 'all' : 'none',
          backgroundColor:
            height !== MIN_HEIGHT ? ` rgb(from var(--toolbar-background) r g b / ${openPercent - 0.1})` : 'unset',
        }}
      />

      <div
        ref={draggableElement}
        onTouchStart={open ? handleTouchStart : undefined}
        onTouchMove={open ? handleTouch : undefined}
        onTouchEnd={open ? handleTouchEnd : undefined}
        style={{ ...style, height, touchAction: !open ? 'none' : 'unset' }}
        className={`
          ${styles.container}
          ${shrunk ? styles.shrunk : ''} 
          ${scrollingDown ? styles.hidden : ''}
          ${className}
        `}>
        <div className={`${styles.toolbar} ${toolbarClassName}`}>
          <button className={styles.dragHandle} onClick={() => open && handleSetOpen(false, maxHeight, null, null)}>
            {open && <span />}
          </button>

          <FadeInOut
            show={!open}
            cssRemovedFromDOMOnClosed
            className={styles.options}
            style={{ gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))` }}>
            {!shrunk &&
              options.map((option, index) => {
                if (option.type === 'blank') return <div key={index} />

                return <FloatingToolbarGridOption key={index} {...option} onClick={() => handleOptionClick(option)} />
              })}

            {includeShrinkOption && (
              <FloatingToolbarGridOption
                icon={shrunk ? 'arrow circle left' : 'arrow circle right'}
                label={shrunk ? 'Toolbar' : 'Hide'}
                onClick={() => handleSetHidden(!shrunk)}
              />
            )}
          </FadeInOut>

          <FadeInOut
            show={open}
            ref={openBodyScrollRef}
            className={styles.openMenuContent}
            outDuration={100}
            cssRemovedFromDOMOnClosed>
            <div
              style={{ opacity: !open || openPercent === 1 ? 1 : openPercent - 0.2 }}
              className={subComponentClassName}>
              {flingMenuOptions && (
                <div className={styles.list}>
                  {flingMenuOptions.map((option, index) => (
                    <FloatingToolbarListOption
                      key={index}
                      {...option}
                      onClick={() => {
                        if (option.onClick) option.onClick()

                        handleSetOpen(false)
                      }}
                    />
                  ))}
                </div>
              )}

              {subComponent &&
                cloneElement(subComponent, { onClose: () => handleSetOpen(false, maxHeight, null, null) })}
            </div>
          </FadeInOut>
        </div>
      </div>
    </>
  )
}

export default FloatingToolbar

export const InternalFloatingNavbar = (props: Props) => (
  <FloatingToolbar noHideOnScroll className={styles.internal} {...props} />
)

const getMaxHeight = (maxHeightOverride?: number) => {
  if (typeof window === 'undefined') return MIN_HEIGHT

  const absoluteMax = window?.innerHeight * TOTAL_MAX_HEIGHT_PERCENT // 80% of the height of the page

  if (maxHeightOverride === undefined) return absoluteMax
  if (absoluteMax < maxHeightOverride) return absoluteMax

  return maxHeightOverride
}
